import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import Spinner from 'components/Spinner';
import Dropdown from 'components/Dropdown';

import { xpackType } from 'types';

import DependenciesIssues from './components/DependenciesIssues';

import {
  StyledName,
  StyledContainer,
  StyledTime,
  StyledIconOptions,
  StyledToggler,
  StyledDropdownItem,
} from './styled';

const XpackItem = props => {
  const {
    overrideAllXpackIntentsIsLoading,
    xpack,
    disableAllXpackIntentsIsLoading,
    deleteXpack,
    downloadXpack,
    isDownloading,
    onOverrideAllXpackIntents,
    onDisableAllXpackIntents,
    updateXpack,
  } = props;

  const dropdownRef = useRef();

  const hideDropdown = () => dropdownRef.current.hide();

  const handleUpdate = () => {
    updateXpack(props.xpack);
    hideDropdown();
  };

  const handleDownload = async () => {
    await downloadXpack(xpack);
    hideDropdown();
  };

  const handleDelete = () => {
    deleteXpack(xpack);
    hideDropdown();
  };

  const handleDeactivateIntents = async value => {
    await onDisableAllXpackIntents(xpack.id, value);
    hideDropdown();
  };

  const handleDeactivateConstraintSets = async value => {
    await onOverrideAllXpackIntents(xpack.id, value);
    hideDropdown();
  };

  const issues = get(xpack, 'dependency_issues', []);
  const hasIssues = issues.length > 1;

  // Toggle value should be equal to xpack.overrideAllXpackIntents.
  // But if overrideAllXpackIntentsIsLoading is true (it means that user clicked on toggler)
  // it should be equal with complement of xpack.overrideAllXpackIntents
  const deactivateConstraintSetsValue = overrideAllXpackIntentsIsLoading
    ? !xpack.has_all_intents_overridden
    : xpack.has_all_intents_overridden;

  const deactivateIntentsTogglerValue = disableAllXpackIntentsIsLoading
    ? !xpack.has_all_intents_deactivated
    : xpack.has_all_intents_deactivated;

  return (
    <StyledContainer hasIssues={hasIssues}>
      <StyledName data-test="name">
        {xpack.name}
        {hasIssues && <DependenciesIssues issues={issues} />}
      </StyledName>
      <StyledTime format="DD/MM/YYYY – HH:mm" timestamp={xpack.created_at} />

      <Dropdown inputRef={dropdownRef} trigger={<StyledIconOptions />}>
        <StyledDropdownItem data-test="update" onClick={handleUpdate}>
          Update
        </StyledDropdownItem>

        <StyledDropdownItem
          data-test="download"
          isDisabled={isDownloading}
          onClick={handleDownload}
        >
          Download
          {isDownloading && <Spinner isBlue />}
        </StyledDropdownItem>

        <hr />

        <StyledDropdownItem data-test="deactivate-intents">
          <StyledToggler
            input={{
              value: deactivateIntentsTogglerValue,
              onChange: handleDeactivateIntents,
            }}
            label="Deactivate Intents"
          />
        </StyledDropdownItem>

        <StyledDropdownItem data-test="deactivate-constraint-sets">
          <StyledToggler
            input={{
              value: deactivateConstraintSetsValue,
              onChange: handleDeactivateConstraintSets,
            }}
            label="Deactivate Constraint Sets"
          />
        </StyledDropdownItem>
        <hr />

        <StyledDropdownItem data-test="delete" onClick={handleDelete}>
          Delete
        </StyledDropdownItem>
      </Dropdown>
    </StyledContainer>
  );
};

XpackItem.propTypes = {
  deleteXpack: PropTypes.func.isRequired,
  disableAllXpackIntentsIsLoading: PropTypes.bool.isRequired,
  downloadXpack: PropTypes.func.isRequired,
  isDownloading: PropTypes.bool.isRequired,
  overrideAllXpackIntentsIsLoading: PropTypes.bool.isRequired,
  updateXpack: PropTypes.func.isRequired,
  xpack: xpackType.isRequired,
  onDisableAllXpackIntents: PropTypes.func.isRequired,
  onOverrideAllXpackIntents: PropTypes.func.isRequired,
};

export default XpackItem;
